/**
 * Farm Workspace v2 — frontend contracts (Phase 0 freeze; docs/farm-workspace/redesign-plan.md §4, §5, §9).
 * Mirrors `src/DRIS.Shared/DTOs/FarmWorkspace/FarmWorkspaceDtos.cs` (camelCase on the wire).
 * Both Phase 1 frontend tracks build against these — change them only with the backend twin.
 */

// ── Tabs & routes ──────────────────────────────────────────────────────────────────────────────

export type WorkspaceTab =
  | 'overview'
  | 'structure'
  | 'batches'
  | 'workflows'
  | 'smart-sampling'
  | 'programs';

/** Ordered as rendered; also the source of truth for `isWorkspaceTab` / `parseTab`. */
export const WORKSPACE_TABS: readonly WorkspaceTab[] = [
  'overview',
  'structure',
  'batches',
  'workflows',
  'smart-sampling',
  'programs',
] as const;

// ── Tree (GET /farm-workspace/farmers/{id}/tree) ───────────────────────────────────────────────

export interface AgentRef {
  id: number;
  name: string;
}

export interface FarmerHeader {
  id: number;
  /** "FirstName Surname" trimmed — same convention as entity search. */
  displayName: string;
  email?: string | null;
  contact?: string | null;
  /** Distinct agents linked to any of the farmer's farms, ordered by name. */
  agents: AgentRef[];
}

export interface PlotTreeNode {
  id: number;
  name: string;
  plantDate?: string | null;
  areaHa?: number | null;
  hasBoundary: boolean;
}

export interface FarmTreeNode {
  id: number;
  name: string;
  country?: string | null;
  plotCount: number;
  /** Sum of plot areas (missing area counts as 0). */
  areaHa: number;
  plots: PlotTreeNode[];
}

export interface FarmerTree {
  farmer: FarmerHeader;
  /** Ordered by name; soft-deleted farms/plots excluded. */
  farms: FarmTreeNode[];
}

// ── Scope / visibility ─────────────────────────────────────────────────────────────────────────

export interface WorkspaceScope {
  farmerId: number;
  farmId?: number;
  plotId?: number;
}

/** Which work-product kinds the current user may see; drives which tabs render (§9.1). */
export interface WorkspaceVisibility {
  batches: boolean;
  runs: boolean;
  programs: boolean;
  workflows: boolean;
}

// ── Summary (GET /farm-workspace/summary) ──────────────────────────────────────────────────────

export interface SampleCounts {
  sap: number;
  soil: number;
  tissue: number;
}

export interface WorkspaceCounts {
  farms: number;
  plots: number;
  areaHa: number;
  samples: SampleCounts;
  batches: number;
  workflowsOpen: number;
  /** ALL Smart Sampling runs in scope (the tab badge / tile headline). */
  runs: number;
  /** Runs currently Queued / Running / CancelRequested (a subset of `runs`). */
  runsInFlight: number;
  programsActive: number;
}

export type ActivityKind = 'Batch' | 'Workflow' | 'SmartSamplingRun' | 'Program';

export type AttentionSeverity = 'info' | 'warning' | 'error';

export interface AttentionItem {
  kind: ActivityKind;
  /** The work product's own id. */
  id: number;
  title: string;
  reason: string;
  severity: AttentionSeverity;
  occurredAt: string;
  /** Server-built deep link — navigate to it verbatim. */
  route: string;
}

export interface WorkspaceSummary {
  scope: WorkspaceScope;
  visibility: WorkspaceVisibility;
  counts: WorkspaceCounts;
  attention: AttentionItem[];
}

// ── Activity (GET /farm-workspace/activity, /recently-active) ──────────────────────────────────

export type ActivityStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'error';

export interface ActivityItem {
  kind: ActivityKind;
  /** The work product's own id. */
  id: number;
  title: string;
  subtitle?: string | null;
  status: string;
  /** Workflows carry `Status.Code` (e.g. `AWAITING_REVIEW`); null/absent for every other kind. */
  statusCode?: string | null;
  statusTone: ActivityStatusTone;
  occurredAt: string;
  farmerId: number;
  farmerName: string;
  farmId?: number | null;
  farmName?: string | null;
  plotNames: string[];
  /** Server-built deep link — navigate to it verbatim. */
  route: string;
}

export interface ActivityPage {
  items: ActivityItem[];
  /** Opaque continuation cursor; null/absent = no more pages. Pass back verbatim. */
  nextCursor?: string | null;
}

export interface RecentlyActiveFarmer {
  farmerId: number;
  farmerName: string;
  latest: ActivityItem;
}

// ── Hub-wide CRUD contract ─────────────────────────────────────────────────────────────────────

export type EntityKind = 'farmer' | 'farm' | 'plot';

/**
 * Hub-wide CRUD actions; implemented once by a `useEntityCrud` hook (Phase 1C-i) and consumed by the
 * rail, strip and structure tables.
 */
export interface EntityActions {
  openCreateFarm: (farmerId: number) => void;
  openCreatePlot: (farmId: number, farmerId: number) => void;
  openEdit: (kind: EntityKind, id: number) => void;
  /** Reparent (farm→farmer, plot→farm); gated by the dependency pre-check. */
  openMove: (kind: 'farm' | 'plot', id: number) => void;
  openDelete: (kind: EntityKind, id: number) => void;
  /**
   * De-duplicate: merge a same-type duplicate into this survivor (Plan A §9). SuperAdmin-only —
   * the menu item renders only when {@link canMerge} is true.
   */
  openMerge: (kind: EntityKind, id: number) => void;
  /** True only for SuperAdmin: gates the Merge menu item AND the dependent-Move dialog. */
  canMerge: boolean;
}
